import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Observable, Subscription, combineLatest, forkJoin, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { NotificationEntity } from 'src/app/models/notification.model';
import { Router } from '@angular/router';
import { FeedbackService } from 'src/app/services/feedback/feedback.service';
import { AppointmentService } from 'src/app/services/appointment/appointment.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store';
import * as UserProfileSelectors from 'src/app/store/user-profile/user-profile.selector';
import * as AppointmentSelectors from 'src/app/store/appointment/appointment.selectors';
import * as AppointmentActions from 'src/app/store/appointment/appointment.actions';
import { AppointmentEntity } from 'src/app/models/appointment.model';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsComponent implements OnInit, OnDestroy {
  notifications$: Observable<NotificationEntity[]>;
  readNotifications$: Observable<NotificationEntity[]>;
  allNotifications: NotificationEntity[] = [];
  filteredNotifications: NotificationEntity[] = [];
  customerFilteredNotifications: NotificationEntity[] = [];
  todayAppointments: AppointmentEntity[] = [];

  activeTab: 'all' | 'unread' | 'read' = 'all';
  isLoading = false;
  private currentUserId: string | null = null;

  private subscription = new Subscription();

  constructor(
    private notificationService: NotificationService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private feedbackService: FeedbackService,
    private appointmentService: AppointmentService,
    private toastService: ToastService,
    private store: Store<AppState>
  ) {
    this.notifications$ = this.notificationService.notifications$;
    this.readNotifications$ = this.notificationService.readNotifications$;
  }

  ngOnInit(): void {
    // Subscribe to user profile to get current user ID
    this.subscription.add(
      this.store
        .select(UserProfileSelectors.selectCurrentUserProfile)
        .subscribe((userProfile) => {
          this.currentUserId = userProfile?.id || null;
        })
    );

    this.loadNotifications();
    this.loadUpcomingAppointments();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private loadUpcomingAppointments(): void {
    // Dispatch action to load upcoming appointments
    this.store.dispatch(AppointmentActions.loadUpcomingAppointments());

    // Subscribe to upcoming appointments from store
    this.subscription.add(
      this.store
        .select(AppointmentSelectors.selectUpcomingAppointments)
        .subscribe((appointments) => {
          this.todayAppointments = appointments;
          this.cdr.markForCheck();
        })
    );
  }
  private loadNotifications(): void {
    this.subscription.add(
      combineLatest([this.notifications$, this.readNotifications$]).subscribe(
        ([unreadNotifications, readNotifications]) => {
          // Filter notifications to match mobile app customer notifications
          const customerUnreadNotifications =
            this.filterCustomerNotifications(unreadNotifications);
          const customerReadNotifications =
            this.filterCustomerNotifications(readNotifications);

          this.allNotifications = [
            ...customerUnreadNotifications,
            ...customerReadNotifications,
          ].sort(
            (a, b) =>
              new Date(b.createdTime).getTime() -
              new Date(a.createdTime).getTime()
          );

          // Filter out feedback reminders if feedback already exists
          this.filterFeedbackReminders(this.allNotifications);
        }
      )
    );
  }

  /**
   * Filter notifications to match mobile app customer notification types
   */
  private filterCustomerNotifications(
    notifications: NotificationEntity[]
  ): NotificationEntity[] {
    return notifications.filter(
      (n) =>
        n.type === 'APPOINTMENT_CONFIRMED' ||
        n.type === 'APPOINTMENT_CANCELED' ||
        n.type === 'APPOINTMENT_CANCELLED' ||
        n.type === 'APPOINTMENT_BOOKED' ||
        n.type === 'APPOINTMENT_CREATED' ||
        n.type === 'APPOINTMENT_REMINDER' ||
        n.type === 'REMINDER' ||
        n.type === 'FEEDBACK_REMINDER' ||
        n.type === 'APPOINTMENT_COMPLETED' ||
        n.type === 'J_POINT_EARNED_CONFIRMATION' ||
        n.type === 'J_POINT_EARNED' ||
        n.type === 'J_POINT_EARNED_FEEDBACK' ||
        n.type === 'J_POINT_EARNED_BOOKING' ||
        n.type === 'J_POINT_EARNED_POST' ||
        n.type === 'J_POINT_EARNED_CHECK_IN' ||
        n.type === 'J_POINT_EARNED_CREATE_ACCOUNT' ||
        n.type === 'GIFT_CARD_TRANSFER' ||
        n.type === 'APPOINTMENT_CHECKED_IN' ||
        n.type === 'RECALL_REMINDER'
    );
  }

  /**
   * Filter out feedback reminder notifications if customer has already provided feedback
   */
  private filterFeedbackReminders(notifications: NotificationEntity[]): void {
    const feedbackReminders = notifications.filter(
      (n) => n.type === 'FEEDBACK_REMINDER'
    );
    const otherNotifications = notifications.filter(
      (n) => n.type !== 'FEEDBACK_REMINDER'
    );

    if (feedbackReminders.length === 0) {
      this.customerFilteredNotifications = notifications;
      this.filterNotifications();
      this.cdr.markForCheck();
      return;
    }

    // Check feedback status for each feedback reminder
    const feedbackChecks = feedbackReminders.map((notification) => {
      const appointmentId = notification.data?.appointmentId;
      if (!appointmentId) {
        return of({ notification, hasFeedback: false });
      }

      return this.checkFeedbackExists(appointmentId).pipe(
        map((hasFeedback) => ({ notification, hasFeedback }))
      );
    });

    forkJoin(feedbackChecks).subscribe({
      next: (
        results: Array<{
          notification: NotificationEntity;
          hasFeedback: boolean;
        }>
      ) => {
        // Filter out notifications where feedback already exists
        const validFeedbackReminders = results
          .filter((result) => !result.hasFeedback)
          .map((result) => result.notification);

        this.customerFilteredNotifications = [
          ...otherNotifications,
          ...validFeedbackReminders,
        ].sort(
          (a, b) =>
            new Date(b.createdTime).getTime() -
            new Date(a.createdTime).getTime()
        );

        this.filterNotifications();
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('[NOTIFICATION] Error checking feedback status:', error);
        // On error, show all notifications to avoid hiding valid reminders
        this.customerFilteredNotifications = notifications;
        this.filterNotifications();
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Check if feedback exists for an appointment
   */
  private checkFeedbackExists(appointmentId: string): Observable<boolean> {
    return this.feedbackService.checkFeedbackExists(appointmentId);
  }

  switchTab(tab: 'all' | 'unread' | 'read'): void {
    this.activeTab = tab;
    this.filterNotifications();
  }

  private filterNotifications(): void {
    const sourceNotifications =
      this.customerFilteredNotifications.length > 0
        ? this.customerFilteredNotifications
        : this.allNotifications;

    switch (this.activeTab) {
      case 'unread':
        this.filteredNotifications = sourceNotifications.filter((n) => !n.read);
        break;
      case 'read':
        this.filteredNotifications = sourceNotifications.filter((n) => n.read);
        break;
      default:
        this.filteredNotifications = sourceNotifications;
        break;
    }
  }

  async onNotificationClick(notification: NotificationEntity): Promise<void> {
    // Mark as read if not already read (similar to mobile app)
    if (!notification.read && notification.type !== 'REMINDER') {
      await this.markAsRead(notification.id);
    }

    // Navigate based on notification type
    this.handleNotificationNavigation(notification);
  }

  /**
   * Mark notification as read (matching mobile app implementation)
   */
  public async markAsRead(notificationId: string): Promise<void> {
    try {
      if (!this.currentUserId) {
        console.warn('No current user ID available');
        return;
      }

      await this.notificationService.markAsRead(notificationId);
      console.log('Notification marked as read:', notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      this.toastService.error('Error', 'Failed to mark notification as read');
    }
  }

  private handleNotificationNavigation(notification: NotificationEntity): void {
    const { data, type } = notification;

    switch (type) {
      case 'APPOINTMENT_CREATED':
      case 'APPOINTMENT_CONFIRMED':
      case 'APPOINTMENT_CANCELED':
      case 'APPOINTMENT_CANCELLED':
      case 'APPOINTMENT_BOOKED':
      case 'APPOINTMENT_REMINDER':
      case 'REMINDER':
      case 'FEEDBACK_REMINDER':
      case 'APPOINTMENT_COMPLETED':
      case 'APPOINTMENT_CHECKED_IN':
        if (data?.appointmentId) {
          this.router.navigate(['/appointments', data.appointmentId]);
        } else {
          this.router.navigate(['/appointments']);
        }
        break;

      case 'J_POINT_EARNED_CONFIRMATION':
      case 'J_POINT_EARNED_CHECK_IN':
      case 'J_POINT_EARNED_CREATE_ACCOUNT':
      case 'J_POINT_EARNED':
      case 'J_POINT_EARNED_FEEDBACK':
      case 'J_POINT_EARNED_BOOKING':
      case 'J_POINT_EARNED_POST':
        // Navigate to account/profile page for j-Points
        this.router.navigate(['/account']);
        break;

      case 'GIFT_CARD_TRANSFER':
        // Navigate to gift cards page
        this.router.navigate(['/gift-cards']);
        break;

      case 'RECALL_REMINDER':
        if (data?.storeId) {
          this.router.navigate(['/businesses', data.storeId]);
        } else {
          this.router.navigate(['/businesses']);
        }
        break;

      case 'ORDER_STATUS_UPDATED':
        if (data?.orderId) {
          this.router.navigate(['/orders', data.orderId]);
        } else {
          this.router.navigate(['/orders']);
        }
        break;

      case 'PROMOTION_AVAILABLE':
        if (data?.storeId) {
          this.router.navigate(['/businesses', data.storeId]);
        }
        break;

      default:
        // Stay on notifications page
        break;
    }
  }

  /**
   * Mark all notifications as read (matching mobile app implementation)
   */
  public async markAllAsRead(): Promise<void> {
    this.isLoading = true;
    this.cdr.markForCheck();

    try {
      if (!this.currentUserId) {
        console.warn('No current user ID available');
        this.toastService.error('Error', 'User not authenticated');
        return;
      }

      await this.notificationService.markAllAsRead();
      console.log('All notifications marked as read');
      this.toastService.success('Success', 'All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      this.toastService.error(
        'Error',
        'Failed to mark all notifications as read'
      );
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  async refreshNotifications(): Promise<void> {
    this.isLoading = true;
    this.cdr.markForCheck();

    try {
      await this.notificationService.refreshNotifications();
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  getNotificationIcon(type: string): string {
    if (type.includes('APPOINTMENT')) return 'fas fa-calendar-check';
    if (type.includes('REMINDER')) return 'fas fa-bell';
    if (type.includes('FEEDBACK')) return 'fas fa-star';
    if (type.includes('PAYMENT')) return 'fas fa-credit-card';
    if (type.includes('J_POINT_EARNED')) return 'fas fa-gem';
    if (type.includes('GIFT_CARD_TRANSFER')) return 'fas fa-gift';
    if (type.includes('RECALL_REMINDER')) return 'fas fa-bell';
    return 'fas fa-info-circle';
  }

  getNotificationColor(type: string): string {
    if (type.includes('APPOINTMENT')) return 'text-primary';
    if (type.includes('REMINDER')) return 'text-warning';
    if (type.includes('PAYMENT')) return 'text-warning';
    if (type.includes('GIFT_CARD_TRANSFER')) return 'text-primary';
    if (type.includes('J_POINT_EARNED')) return 'text-success';
    if (type.includes('RECALL_REMINDER')) return 'text-warning';
    return 'text-muted';
  }

  getNotificationTitle(notification: NotificationEntity): string {
    switch (notification.type) {
      case 'APPOINTMENT_CONFIRMED':
        return 'Appointment Confirmed';
      case 'APPOINTMENT_CANCELLED':
      case 'APPOINTMENT_CANCELED':
        return 'Appointment Cancelled';
      case 'APPOINTMENT_CREATED':
      case 'APPOINTMENT_BOOKED':
        return 'New Appointment';
      case 'APPOINTMENT_CHECKED_IN':
        return 'Appointment Checked In';
      case 'REMINDER':
      case 'APPOINTMENT_REMINDER':
        return 'Appointment Reminder';
      case 'FEEDBACK_REMINDER':
        return 'Feedback Reminder';
      case 'APPOINTMENT_COMPLETED':
        return 'Appointment Completed';
      case 'J_POINT_EARNED_CONFIRMATION':
      case 'J_POINT_EARNED':
      case 'J_POINT_EARNED_FEEDBACK':
      case 'J_POINT_EARNED_BOOKING':
      case 'J_POINT_EARNED_POST':
      case 'J_POINT_EARNED_CHECK_IN':
      case 'J_POINT_EARNED_CREATE_ACCOUNT':
        return 'j-Points Earned!';
      case 'GIFT_CARD_TRANSFER':
        return 'Gift Card Received!';
      case 'RECALL_REMINDER':
        return 'Recall Reminder';
      default:
        return 'Notification';
    }
  }

  getNotificationMessage(notification: NotificationEntity): string {
    switch (notification.type) {
      case 'APPOINTMENT_CONFIRMED':
        return `Your appointment with ${
          notification.data?.staff
            ?.map((staff: any) => staff.displayName)
            ?.join(', ') || 'staff'
        } has been confirmed`;
      case 'APPOINTMENT_CANCELLED':
      case 'APPOINTMENT_CANCELED':
        return `Appointment with ${
          notification.data?.staff
            ?.map((s: any) => s.displayName)
            ?.join(', ') || 'staff'
        } has been cancelled`;
      case 'APPOINTMENT_CREATED':
      case 'APPOINTMENT_BOOKED':
        return `You have a new appointment with ${
          notification.data?.staffNames || 'staff'
        }`;
      case 'J_POINT_EARNED_CONFIRMATION':
        return `You have earned ${
          Number(notification.data?.pointsEarned).toFixed(2) || '0'
        } j-Points for confirming your appointment!`;
      case 'J_POINT_EARNED':
        return `You have earned ${
          Number(notification.data?.pointsEarned).toFixed(2) || '0'
        } j-Points for your payment!`;
      case 'J_POINT_EARNED_FEEDBACK':
        return `You have earned ${
          Number(notification.data?.pointsEarned).toFixed(2) || '0'
        } j-Points for your feedback!`;
      case 'J_POINT_EARNED_BOOKING':
        return `You have earned ${
          Number(notification.data?.pointsEarned).toFixed(2) || '0'
        } j-Points for booking services!`;
      case 'J_POINT_EARNED_POST':
        return `You have earned ${
          Number(notification.data?.pointsEarned).toFixed(2) || '0'
        } j-Points for posting pictures!`;
      case 'J_POINT_EARNED_CHECK_IN':
        return `You have earned ${
          Number(notification.data?.pointsEarned).toFixed(2) || '0'
        } j-Points for checking in!`;
      case 'J_POINT_EARNED_CREATE_ACCOUNT':
        return `You have earned ${
          Number(notification.data?.pointsEarned).toFixed(2) || '0'
        } j-Points for creating an account!`;
      case 'APPOINTMENT_CHECKED_IN':
        return `You have checked in for your appointment at ${
          notification.data?.storeName || 'the store'
        }`;
      case 'REMINDER':
      case 'APPOINTMENT_REMINDER':
        const appointmentDate = notification.data?.date
          ? new Date(notification.data.date)
          : new Date();
        return `You have an appointment on ${appointmentDate.toLocaleDateString(
          'en-US',
          {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }
        )} at ${appointmentDate.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })} at ${notification.data?.storeName || 'the store'}`;
      case 'FEEDBACK_REMINDER':
        return `Don't forget to leave feedback for your appointment at ${
          notification.data?.storeName || 'the store'
        }!`;
      case 'APPOINTMENT_COMPLETED':
        return `Thank you for your appointment at ${
          notification.data?.storeName || 'the store'
        }! We hope you had a great experience.`;
      case 'GIFT_CARD_TRANSFER':
        return `You have received a gift card from ${
          notification.data?.senderInfo?.name || 'someone'
        }!`;
      case 'RECALL_REMINDER':
        return `Time for your next appointment at ${
          notification.data?.storeName || 'the store'
        }!`;
      default:
        return notification.message || 'You have a new notification';
    }
  }

  formatTime(date: Date): string {
    return this.notificationService.formatNotificationTime(date);
  }

  formatFullTime(date: Date): string {
    return new Date(date).toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  getTabCount(tab: 'all' | 'unread' | 'read'): number {
    const sourceNotifications =
      this.customerFilteredNotifications.length > 0
        ? this.customerFilteredNotifications
        : this.allNotifications;

    switch (tab) {
      case 'unread':
        return sourceNotifications.filter((n) => !n.read).length;
      case 'read':
        return sourceNotifications.filter((n) => n.read).length;
      default:
        return sourceNotifications.length;
    }
  }

  /**
   * Check if notification is a reminder that can be acted upon
   */
  isActionableReminder(notification: NotificationEntity): boolean {
    if (notification.type !== 'REMINDER') return false;
    if (!notification.data?.appointmentId) return false;

    const appointment = this.todayAppointments.find(
      (n) => n.id === notification.data?.appointmentId
    );

    if (appointment) {
      return true;
    }

    // Check if appointment date has passed
    if (this.isAppointmentPast(notification.data?.date)) {
      return false;
    }
    return false;
  }

  /**
   * Check if appointment date has passed
   */
  private isAppointmentPast(appointmentDate: string): boolean {
    if (!appointmentDate) return true;

    try {
      const apptDate = new Date(appointmentDate);
      const now = new Date();
      return apptDate.getTime() < now.getTime();
    } catch (error) {
      console.error('Error parsing appointment date:', error);
      return true;
    }
  }

  /**
   * Handle confirm appointment action
   */
  async confirmAppointment(
    notification: NotificationEntity,
    event: Event
  ): Promise<void> {
    event.stopPropagation(); // Prevent notification click

    this.isLoading = true;
    this.cdr.markForCheck();

    try {
      const appointmentId = notification.data?.appointmentId;
      if (!appointmentId) {
        this.toastService.error('Error', 'Appointment ID not found.');
        return;
      }

      console.log('✅ Confirming appointment:', appointmentId);

      // Call backend API to confirm appointment
      await this.appointmentService
        .confirmAppointment(appointmentId)
        .toPromise();

      // Mark notification as read
      await this.markAsRead(notification.id);

      // Show success message
      this.toastService.success(
        'Success',
        'Appointment confirmed successfully!'
      );

      // Refresh notifications
      await this.refreshNotifications();
    } catch (error) {
      console.error('❌ Error confirming appointment:', error);
      this.toastService.error(
        'Error',
        'Failed to confirm appointment. Please try again.'
      );
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Handle cancel appointment action
   */
  async cancelAppointment(
    notification: NotificationEntity,
    event: Event
  ): Promise<void> {
    event.stopPropagation(); // Prevent notification click

    // Show confirmation dialog
    if (!confirm('Are you sure you want to cancel this appointment?')) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    try {
      const appointmentId = notification.data?.appointmentId;
      if (!appointmentId) {
        this.toastService.error('Error', 'Appointment ID not found.');
        return;
      }

      console.log('❌ Cancelling appointment:', appointmentId);

      // Call backend API to cancel appointment
      await this.appointmentService
        .cancelAppointment(appointmentId)
        .toPromise();

      // Mark notification as read
      await this.markAsRead(notification.id);

      // Show success message
      this.toastService.success(
        'Success',
        'Appointment cancelled successfully'
      );

      // Refresh notifications
      await this.refreshNotifications();
    } catch (error) {
      console.error('❌ Error cancelling appointment:', error);
      this.toastService.error(
        'Error',
        'Failed to cancel appointment. Please try again.'
      );
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  trackByNotificationId(
    index: number,
    notification: NotificationEntity
  ): string {
    return notification.id;
  }
}
